import * as Sentry from '@sentry/react';
import { apis } from 'utils/lib/apis';
import {
    signOut as firebaseSignOut,
    getAuth,
    signInWithCustomToken,
    browserSessionPersistence,
    setPersistence,
    User
} from 'firebase/auth';
import { getFirestore, doc, serverTimestamp, setDoc, Firestore } from 'firebase/firestore';
import {
    ref as databaseRef,
    getDatabase,
    onValue,
    set as databaseSet,
    get as databaseGet,
    Database
} from 'firebase/database';
import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { firebaseApp } from 'firebaseConfig';
import { useClassBooks } from 'components/hooks/books/useClassBooks';

const auth = getAuth(firebaseApp);
export const get_access_key = `prod/get_access_token`;
const defaultFirestore = getFirestore(firebaseApp);
const defaultDatabase = getDatabase(firebaseApp);

interface FirebaseContextValue {
    firebaseUser: User | null;
    firestore: Firestore;
    database: Database;
    onValue: typeof onValue;
    databaseRef: typeof databaseRef;
    databaseSet: typeof databaseSet;
    databaseGet: typeof databaseGet;
    signInWithCustomTokenApi: (userId: string, retryCount?: number) => Promise<void>;
    signOutUser: () => Promise<void>;
}

const defaultContextValue: FirebaseContextValue = {
    firebaseUser: null,
    firestore: defaultFirestore,
    database: defaultDatabase,
    onValue,
    databaseRef,
    databaseSet,
    databaseGet,
    signInWithCustomTokenApi: async () => {},
    signOutUser: async () => {}
};

const FirebaseContext = createContext<FirebaseContextValue>(defaultContextValue);

export const useFirebase = () => useContext(FirebaseContext);

interface FirebaseProviderProps {
    children: ReactNode;
}

interface GetAccessTokenResponse {
    hassuccessed?: boolean;
    data?: string;
}

export const FirebaseProvider = ({ children }: FirebaseProviderProps) => {
    const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
    const firestore = useMemo(() => getFirestore(firebaseApp), []);
    const database = useMemo(() => getDatabase(firebaseApp), []);
    const { clearLoadingState } = useClassBooks();

    const signInWithCustomTokenApi = async (userId: string, retryCount = 0): Promise<void> => {
        try {
            if (!userId) {
                console.warn("No userId provided for Firebase authentication");
                return;
            }
            const session_id = sessionStorage.getItem("session_id");
            const dataa = {
                id: userId,
                session_id
            };
            const response = await apis.post('firebase', `/get_access_token`, {
                body: dataa
            });
            const responseBody: GetAccessTokenResponse = JSON.parse(response.body);
            if (responseBody.hassuccessed && responseBody.data) {
                await setPersistence(auth, browserSessionPersistence);
                await signInWithCustomToken(auth, responseBody.data);
            } else {
                console.warn("Firebase authentication failed:", responseBody);
            }
        } catch (error) {
            if (retryCount < 2) {
                setTimeout(() => {
                    signInWithCustomTokenApi(userId, retryCount + 1);
                }, 2000);
            } else {
                console.error('Error during Firebase sign-in:', error);
                Sentry.captureException(`100 Error during Firebase signInWithCustomToken ${error}.`);
            }
        }
    };

    const markInActive = async (): Promise<void> => {
        try {
            const last_log = sessionStorage.getItem('last_log');
            const last_log_user = sessionStorage.getItem('last_log_user');
            const last_log_class = sessionStorage.getItem('last_log_class');
            if (last_log && last_log_user && last_log_class) {
                const logRef = doc(firestore, last_log, last_log_user);
                const logEntry = {
                    userId: last_log_user,
                    classId: last_log_class,
                    lastVisitedPage: '/',
                    timestamp: serverTimestamp(),
                    query: {},
                    active: false
                };
                await setDoc(logRef, logEntry, { merge: true });
                sessionStorage.removeItem('last_log');
                sessionStorage.removeItem('last_log_user');
                sessionStorage.removeItem('last_log_class');
            }
        } catch (error) {
            Sentry.captureException(`101 Error during Firebase markInActive ${error}.`);
        }
    };

    const signOutUser = async (): Promise<void> => {
        try {
            await markInActive();
            clearLoadingState();
            await firebaseSignOut(auth);
        } catch (error) {
            console.error('Error during Firebase sign-out:', error);
            Sentry.captureException(`102 Error during Firebase signOut ${error}.`);
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            setFirebaseUser(user);
        });

        return () => unsubscribe();
    }, []);

    const value: FirebaseContextValue = useMemo(
        () => ({
            firebaseUser,
            firestore,
            database,
            onValue,
            databaseRef,
            databaseSet,
            databaseGet,
            signInWithCustomTokenApi,
            signOutUser
        }),
        [firebaseUser, firestore, database]
    );

    return (
        <FirebaseContext.Provider value={value}>
            {children}
        </FirebaseContext.Provider>
    );
};

export default FirebaseProvider;
